/**
 * Display-only rename of rolling time-period labels.
 * Computation / API / FOM continue to use MMA (3MMA, 4MMA, 6MMA, 12MMA).
 */

const MMA_TO_ROLLING_AVE: ReadonlyArray<readonly [string, string]> = [
  ['12MMA', '12 Rolling Ave'],
  ['9MMA', '9 Rolling Ave'],
  ['6MMA', '6 Rolling Ave'],
  ['4MMA', '4 Rolling Ave'],
  ['3MMA', '3 Rolling Ave'],
];

/** Exact token map for whole-segment labels (e.g. Parent / Level1). */
const MMA_SEGMENT_MAP: Record<string, string> = {
  '3MMA': '3 Rolling Ave',
  '4MMA': '4 Rolling Ave',
  '6MMA': '6 Rolling Ave',
  '9MMA': '9 Rolling Ave',
  '12MMA': '12 Rolling Ave',
};

/**
 * Replaces MMA tokens inside any user-facing string (headers, summaries, chips).
 * Leaves non-MMA text unchanged. Safe for pipe-delimited values like `3MMA|Sep_2022`.
 */
export function formatMmaLabelForDisplay(value: string | null | undefined): string {
  if (value == null || value === '') {
    return value as string;
  }

  let result = String(value);
  for (const [mma, display] of MMA_TO_ROLLING_AVE) {
    if (result.includes(mma)) {
      result = result.split(mma).join(display);
    }
  }
  return result;
}

/** Maps a single segment (exact match) such as Level1 `3MMA`. */
export function formatMmaSegmentForDisplay(segment: string | null | undefined): string {
  if (segment == null || segment === '') {
    return segment as string;
  }
  const trimmed = String(segment).trim();
  return MMA_SEGMENT_MAP[trimmed] ?? trimmed;
}

/**
 * Formats pipe-delimited time-period / hierarchy labels for UI tables and charts.
 * Example: `3MMA|Sep_2022` → `3 Rolling Ave : Sep_2022` when joinWith is ` : `.
 */
export function formatTimePeriodLabelForDisplay(
  value: string | null | undefined,
  joinWith: string = ' : ',
): string {
  if (value == null || value === '') {
    return value as string;
  }

  const text = String(value);
  if (!text.includes('|')) {
    return formatMmaLabelForDisplay(text);
  }

  return text
    .split('|')
    .map((part) => formatMmaSegmentForDisplay(part.trim()) || part.trim())
    .filter((part) => part.length > 0)
    .join(joinWith);
}
